import { useState } from 'react'
import { InfoTip } from '../../components/InfoTip'
import { MobileCard, MobileOnly, DesktopOnly } from '../../components/MobileCard'
import { useExecutions } from '../../context/ExecutionContext'
import { useToast } from '../../context/ToastContext'
import { exportToExcel } from '../../utils/exportExcel'
import { getQty, formatLotsQty } from '../../utils/lotSize'
import type { StrategyExecution } from '../../types'

const getTraderName = (id: string) => {
  if (id === 'trader1' || id === 'trader-1') return 'Trader Alpha'
  if (id === 'trader2' || id === 'trader-2') return 'Trader Beta'
  if (id === 'trader3' || id === 'trader-3') return 'Trader Gamma'
  return id
}

export function AdminExecutions() {
  const { executions, stopExecution } = useExecutions()
  const { addToast } = useToast()
  const [viewModal, setViewModal] = useState<StrategyExecution | null>(null)
  const [stopConfirm, setStopConfirm] = useState<string | null>(null)

  const handleStop = (id: string) => {
    const exec = executions.find((e) => e.id === id)
    stopExecution(id)
    addToast(`Execution "${exec?.setName || id}" stopped`, 'warning')
    setStopConfirm(null)
  }

  return (
    <div>
      <div className="page-head">
        <h1>Strategy Executions <InfoTip term="executionSet" placement="bottom" /></h1>
        <p className="page-sub">Monitor all execution sets across all traders</p>
        <button className="btn btn-sm btn-ghost" style={{ marginTop: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
          onClick={() => {
            exportToExcel(executions.map(e => ({
              Set: e.setName,
              Strategy: e.strategyType.replace('_', ' '),
              Trader: getTraderName(e.traderId),
              Underlying: e.underlying,
              Expiry: (e.params as any).expiryType || 'weekly',
              Strike: (e.params as any).strikeSelectionMode || 'ATM',
              Lots: (e.params as any).quantityLots ?? '',
              Qty: (e.params as any).quantityLots ? getQty((e.params as any).quantityLots, e.underlying) : '',
              Status: e.status,
              Premium: e.state.currentPremium,
              PnL: e.state.pnl,
              ReEntries: e.state.reentryCount,
              Broker: e.brokerId || '',
              Mode: e.mode.replace('_', ' '),
            })), `LaMoney_Executions_${new Date().toISOString().split('T')[0]}`)
            addToast('Excel exported successfully', 'success')
          }}>
          ⬇ Export Excel
        </button>
      </div>

      <DesktopOnly>
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Set <InfoTip term="executionSet" /></th>
                <th>Strategy</th>
                <th>Trader</th>
                <th>Underlying <InfoTip term="underlying" /></th>
                <th>Expiry</th>
                <th>Strike</th>
                <th>Lots / Qty <InfoTip term="lots" /></th>
                <th>Status <InfoTip term="status" /></th>
                <th>Premium</th>
                <th>P&L <InfoTip term="pnl" /></th>
                <th>Re-entries</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((e) => (
                <tr key={e.id}>
                  <td><strong>{e.setName}</strong></td>
                  <td><code style={{ fontSize: '0.75rem' }}>{e.strategyType.replace('_', ' ')}</code></td>
                  <td><span className="badge badge-sl">{getTraderName(e.traderId)}</span></td>
                  <td>{e.underlying}</td>
                  <td><span className="badge badge-entry" style={{ fontSize: '0.6875rem' }}>{(e.params as any).expiryType || 'weekly'}</span></td>
                  <td><code style={{ fontSize: '0.6875rem' }}>{(e.params as any).strikeSelectionMode || 'ATM'}{(e.params as any).strikeOffset ? `${(e.params as any).strikeOffset > 0 ? '+' : ''}${(e.params as any).strikeOffset}` : ''}</code></td>
                  <td>{(e.params as any).quantityLots ? <><span style={{ fontWeight: 600 }}>{(e.params as any).quantityLots}</span> <span style={{ color: 'var(--text-muted)', fontSize: '0.6875rem' }}>({getQty((e.params as any).quantityLots, e.underlying)} qty)</span></> : '—'}</td>
                  <td>
                    <span className={`badge ${e.status === 'running' ? 'badge-target' : e.status === 'paused' ? 'badge-entry' : 'badge-sl'}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="stat-value accent">₹{e.state.currentPremium}</td>
                  <td>
                    <span className={`stat-value ${e.state.pnl >= 0 ? 'green' : 'red'}`}>
                      {e.state.pnl >= 0 ? '+' : ''}₹{e.state.pnl}
                    </span>
                  </td>
                  <td>{e.state.reentryCount}</td>
                  <td>
                    <button className="btn btn-sm btn-ghost" onClick={() => setViewModal(e)}>View</button>
                    {e.status === 'running' && (
                      <button className="btn btn-sm btn-ghost" style={{ color: 'var(--red)', marginLeft: '0.25rem' }}
                        onClick={() => setStopConfirm(e.id)}>
                        Stop
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DesktopOnly>

      <MobileOnly>
        <div className="m-card-list">
          {executions.map((e) => (
            <MobileCard
              key={e.id}
              title={e.setName}
              subtitle={`${e.strategyType.replace('_', ' ')} · ${getTraderName(e.traderId)}`}
              status={{
                label: e.status,
                variant: e.status === 'running' ? 'green' : e.status === 'paused' ? 'yellow' : 'red',
              }}
              fields={[
                {
                  label: 'P&L',
                  value: `${e.state.pnl >= 0 ? '+' : ''}₹${e.state.pnl}`,
                  accent: e.state.pnl >= 0 ? 'green' : 'red',
                  bold: true,
                },
                {
                  label: 'MTM',
                  value: `₹${e.state.mtm.totalPnl}`,
                  accent: e.state.mtm.totalPnl >= 0 ? 'green' : 'red',
                },
                {
                  label: 'Lots',
                  value: (e.params as any).quantityLots
                    ? `${(e.params as any).quantityLots} (${getQty((e.params as any).quantityLots, e.underlying)} qty)`
                    : '—',
                },
                {
                  label: 'Underlying',
                  value: e.underlying,
                },
              ]}
              actions={[
                {
                  label: 'View',
                  icon: '📋',
                  variant: 'ghost',
                  onClick: () => setViewModal(e),
                },
                ...(e.status === 'running'
                  ? [
                      {
                        label: 'Stop',
                        icon: '■',
                        variant: 'danger' as const,
                        onClick: () => setStopConfirm(e.id),
                      },
                    ]
                  : []),
              ]}
            />
          ))}
        </div>
      </MobileOnly>

      {/* View Execution Details Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={() => setViewModal(null)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Execution — {viewModal.setName}</h3>
              <button className="modal-close" onClick={() => setViewModal(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <div className="cfg-grid" style={{ marginBottom: '1rem' }}>
                <div className="cfg-field">
                  <label className="cfg-label">Set Name</label>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.875rem' }}>{viewModal.setName}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Strategy</label>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.875rem' }}>{viewModal.strategyType.replace('_', ' ')}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Trader</label>
                  <div>{getTraderName(viewModal.traderId)}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Underlying <InfoTip term="underlying" /></label>
                  <div>{viewModal.underlying}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Expiry</label>
                  <div>{(viewModal.params as any).expiryType || 'weekly'}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Position Type</label>
                  <div>{(viewModal.params as any).positionType || 'straddle'}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Strike</label>
                  <div>{(viewModal.params as any).strikeSelectionMode || 'ATM'}{(viewModal.params as any).strikeOffset ? ` (offset: ${(viewModal.params as any).strikeOffset > 0 ? '+' : ''}${(viewModal.params as any).strikeOffset})` : ''}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Lots / Qty <InfoTip term="lots" /></label>
                  <div>{(viewModal.params as any).quantityLots ? formatLotsQty((viewModal.params as any).quantityLots, viewModal.underlying) : '—'}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Mode <InfoTip term="mode" /></label>
                  <div>{viewModal.mode.replace('_', ' ')}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Broker</label>
                  <div>{viewModal.brokerId || '—'}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Order Type</label>
                  <div>{(viewModal.params as any).orderType || 'MARKET'}</div>
                </div>
                <div className="cfg-field">
                  <label className="cfg-label">Square-off</label>
                  <div>{(viewModal.params as any).squareOffTime || '—'}</div>
                </div>
              </div>

              <h4 style={{ color: 'var(--text-primary)', fontSize: '0.875rem', marginBottom: '0.75rem' }}>Live State</h4>
              <div className="stats-grid" style={{ marginBottom: '1rem' }}>
                <div className="stats-card" style={{ '--card-accent': 'var(--accent)' } as React.CSSProperties}>
                  <div className="stats-card-top"><span className="stats-title">Premium</span></div>
                  <div className="stats-value-wrap"><span className="stats-value">₹{viewModal.state.currentPremium}</span></div>
                  <div className="stats-subtitle">Initial: ₹{viewModal.state.initialPremium}</div>
                </div>
                <div className="stats-card" style={{ '--card-accent': viewModal.state.pnl >= 0 ? 'var(--green)' : 'var(--red)' } as React.CSSProperties}>
                  <div className="stats-card-top"><span className="stats-title">P&L <InfoTip term="pnl" /></span></div>
                  <div className="stats-value-wrap">
                    <span className="stats-value" style={{ color: viewModal.state.pnl >= 0 ? 'var(--green)' : 'var(--red)', WebkitTextFillColor: viewModal.state.pnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                      {viewModal.state.pnl >= 0 ? '+' : ''}₹{viewModal.state.pnl}
                    </span>
                  </div>
                </div>
                <div className="stats-card" style={{ '--card-accent': 'var(--purple)' } as React.CSSProperties}>
                  <div className="stats-card-top"><span className="stats-title">SL / Target</span></div>
                  <div className="stats-value-wrap"><span className="stats-value" style={{ fontSize: '1.25rem' }}>₹{viewModal.state.slPrice.toFixed(0)} / ₹{viewModal.state.targetPrice.toFixed(0)}</span></div>
                </div>
                <div className="stats-card" style={{ '--card-accent': 'var(--yellow)' } as React.CSSProperties}>
                  <div className="stats-card-top"><span className="stats-title">Re-entries</span></div>
                  <div className="stats-value-wrap"><span className="stats-value">{viewModal.state.reentryCount}</span></div>
                </div>
              </div>


              <h4 style={{ color: 'var(--text-primary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>MTM Breakdown <InfoTip term="mtm" /></h4>
              <div className="cfg-grid">
                <div className="param-row">
                  <span className="param-key">Realized P&L</span>
                  <span className="param-val" style={{ color: viewModal.state.mtm.realizedPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    ₹{viewModal.state.mtm.realizedPnl}
                  </span>
                </div>
                <div className="param-row">
                  <span className="param-key">Unrealized P&L</span>
                  <span className="param-val" style={{ color: viewModal.state.mtm.unrealizedPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    ₹{viewModal.state.mtm.unrealizedPnl}
                  </span>
                </div>
                <div className="param-row">
                  <span className="param-key">Total MTM</span>
                  <span className="param-val" style={{ color: viewModal.state.mtm.totalPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    ₹{viewModal.state.mtm.totalPnl}
                  </span>
                </div>
                <div className="param-row">
                  <span className="param-key">Last Updated</span>
                  <span className="param-val">{viewModal.state.mtm.lastUpdated ? new Date(viewModal.state.mtm.lastUpdated).toLocaleString() : '—'}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setViewModal(null)}>Close</button>
              {viewModal.status === 'running' && (
                <button className="btn btn-red" onClick={() => { setViewModal(null); setStopConfirm(viewModal.id) }}>
                  ■ Stop Execution <InfoTip term="panicExit" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stop Confirmation Modal */}
      {stopConfirm && (() => {
        const exec = executions.find((e) => e.id === stopConfirm)
        return (
          <div className="modal-overlay" onClick={() => setStopConfirm(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 style={{ color: 'var(--red)' }}>⚠ Stop Execution <InfoTip term="panicExit" /></h3>
                <button className="modal-close" onClick={() => setStopConfirm(null)}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginBottom: '1rem' }}>
                  Are you sure you want to stop <strong>{exec?.setName || stopConfirm}</strong>?
                  This will square off all open positions for this set.
                </p>
                <div style={{ background: 'var(--red-glow)', padding: '0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.8125rem' }}>
                  Current P&L: <strong style={{ color: (exec?.state.pnl ?? 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {(exec?.state.pnl ?? 0) >= 0 ? '+' : ''}₹{exec?.state.pnl ?? 0}
                  </strong>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setStopConfirm(null)}>Cancel</button>
                <button className="btn btn-red" onClick={() => handleStop(stopConfirm)}>
                  ■ Confirm Stop
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
